import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Caption } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { Header } from '@sorare/core/src/components/dialog/Header';
import CompactBanner from '@sorare/core/src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/CompactBanner';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { useWalletNeedsRecover } from '@sorare/core/src/hooks/recovery/useWalletNeedsRecover';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { usePaymentContext } from 'components/buyActions/Context';
import SummaryTableV2 from 'components/buyActions/PaymentBox/SummaryTableV2';
import { useBuyConfirmationContext } from 'contexts/buyingConfirmation';

import BidContent from './BidContent';
import CurrencyTabs from './CurrencyTabs';
import Methods from './Methods';
import usePaymentBoxEvents from './usePaymentBoxEvents';

interface BaseProps {
  title: ReactNode;
  tokenPreview: ReactNode;
  outBidByAutoBid?: boolean;
  loadingPolling?: boolean;
  setOutBidCallback?: (callback: (amount: string) => void) => void;
  banner?: ReactNode;
}

export interface CloseOnlyProps extends BaseProps {
  onClose: () => void;
  onBack?: never;
}

export interface WithBackProps extends BaseProps {
  onClose?: never;
  onBack: () => void;
}

export type Props = WithBackProps | CloseOnlyProps;

const Content = styled(FullWidthContainer)`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  padding-top: var(--double-unit);
  padding-bottom: var(--double-unit);
`;

const Footer = styled(Vertical).attrs({ gap: 0 })`
  border-top: 1px solid var(--c-nd-50);
  position: sticky;
  margin-left: calc(-1 * var(--container-padding));
  margin-right: calc(-1 * var(--container-padding));
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--current-background-color);
  margin-top: auto;
  @media ${laptopAndAbove} {
    margin-top: 0;
  }
`;

const ButtonWrapper = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit) var(--container-padding);
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: minmax(10px, 1fr);
  --top-offset: 72px;
  height: 100%;
  overflow: auto;
  @media ${laptopAndAbove} {
    --top-offset: 80px;
    height: calc(var(--100vh) - var(--top-offset));
    max-height: calc(var(--100vh) - var(--top-offset));
    grid-template-columns: 1fr 580px;
    > *:first-child {
      height: max-content;
      min-height: calc(100vh - 80px);
      position: sticky;
      top: 0;
    }
  }
  > *:nth-child(2) {
    padding-bottom: 0;
  }
`;

export const PaymentBoxTwoColumnsLayout = ({
  title,
  children,
  tokenPreview,
  onBack,
  onClose,
}: {
  title: ReactNode;
  children: ReactNode;
  tokenPreview: ReactNode;
  onBack?: () => void;
  onClose?: () => void;
}) => {
  const isLaptop = useIsLaptop();
  const { up: upTablet } = useScreenSize('tablet');
  return (
    <Dialog
      open
      maxWidth="lg"
      fullWidth
      onBack={onBack}
      onClose={onClose}
      fullScreen={!upTablet}
      hideHeader={isLaptop}
      title={title}
    >
      <TwoColumn>
        {tokenPreview}
        <Content>
          {isLaptop && (
            <Header onBack={onBack} title={title} onClose={onClose} />
          )}
          {children}
        </Content>
      </TwoColumn>
    </Dialog>
  );
};

export const PaymentBox = (props: Props) => {
  const { formatMessage } = useIntl();

  const { setBuyConfirmationProps } = useBuyConfirmationContext();
  const {
    onClose,
    onBack,
    banner,
    loadingPolling,
    tokenPreview,
    title,
    outBidByAutoBid = false,
    setOutBidCallback,
  } = props;
  const {
    auction,
    amountTooLow,
    sport,
    cta,
    monetaryAmount,
    isFiat,
    displayEth,
    setDisplayEth,
    payWithConversionCreditsOnly,
    referenceCurrency,
    onlyCreditCards,
    selectedOptionConfig,
    setFeesRate,
    errors,
    seller,
    warnings,
    objectId,
    confirmationProviderStateProps,
  } = usePaymentContext();

  const { topUpWalletWithCC } = selectedOptionConfig || {};

  useEffect(() => {
    setFeesRate(selectedOptionConfig?.getFees?.() || 0);
  }, [selectedOptionConfig, setFeesRate]);

  const { currentUser } = useCurrentUserContext();
  const walletNeedsRecover = useWalletNeedsRecover();

  const { trackConfirmPurchaseForm } = usePaymentBoxEvents({
    sport,
    monetaryAmount,
    fiatPayment: isFiat,
    objectId,
  });

  const doSubmit = useCallback(async () => {
    if (confirmationProviderStateProps) {
      setBuyConfirmationProps({
        ...confirmationProviderStateProps,
      });
    }
    selectedOptionConfig?.onSubmit?.();
  }, [
    confirmationProviderStateProps,
    selectedOptionConfig,
    setBuyConfirmationProps,
  ]);

  const isDisabled = useMemo(() => {
    const disableDueToPayment = auction && amountTooLow;
    const disableDueToSelectedOption =
      selectedOptionConfig?.buttonProps?.disabled;

    // isZero: For 100% conversion credit, user don't have to supply a payment method.
    const disableDueToPaymentMethod =
      !payWithConversionCreditsOnly && disableDueToSelectedOption;

    const disableDueToUnconfirmedDevice =
      !isFiat && !currentUser?.confirmedDevice;

    const disableDueToPendingRecoveryWallet = !isFiat && walletNeedsRecover;

    return (
      disableDueToUnconfirmedDevice ||
      disableDueToPaymentMethod ||
      disableDueToPayment ||
      disableDueToPendingRecoveryWallet
    );
  }, [
    auction,
    amountTooLow,
    isFiat,
    selectedOptionConfig?.buttonProps?.disabled,
    payWithConversionCreditsOnly,
    currentUser?.confirmedDevice,
    walletNeedsRecover,
  ]);

  const submit = useCallback(() => {
    trackConfirmPurchaseForm();
    doSubmit();
  }, [doSubmit, trackConfirmPurchaseForm]);

  const isLoading = !!(loadingPolling || selectedOptionConfig?.loading);

  const ctaNode =
    !isDisabled && selectedOptionConfig?.button ? (
      selectedOptionConfig?.button
    ) : (
      <LoadingButton
        size="medium"
        loading={isLoading}
        color="primary"
        disabled={isDisabled}
        onClick={submit}
      >
        <FormattedMessage {...cta} />
      </LoadingButton>
    );

  const titleNode = <div className="text-center">{title}</div>;

  return (
    <PaymentBoxTwoColumnsLayout
      tokenPreview={tokenPreview}
      title={titleNode}
      onBack={onBack}
      onClose={onClose}
    >
      <>
        <CurrencyTabs />
        {auction && (
          <BidContent
            displayEth={displayEth}
            setDisplayEth={setDisplayEth}
            referenceCurrency={referenceCurrency}
            isLoading={isLoading}
            auction={auction}
            outBidByAutoBid={outBidByAutoBid}
            setOutBidCallback={setOutBidCallback}
            topUpWalletWithCC={topUpWalletWithCC}
          />
        )}
        {!payWithConversionCreditsOnly && (
          <Methods onlyCreditCards={onlyCreditCards} />
        )}
        <SummaryTableV2 />
        {errors.length > 0 && (
          <Caption color="var(--c-red-600)">
            {errors
              .map(error => {
                if (typeof error === 'string') return error;
                return formatMessage(error);
              })
              .join(', ')}
          </Caption>
        )}
        {warnings.length > 0 && (
          <Caption color="var(--c-red-600)">
            {warnings
              .map(warning => {
                if (typeof warning === 'string') return warning;
                return formatMessage(warning);
              })
              .join(', ')}
          </Caption>
        )}
        {!seller && <CompactBanner showDepositCta showInprogress />}

        <Footer>
          {banner}
          <ButtonWrapper>{ctaNode}</ButtonWrapper>
        </Footer>
      </>
    </PaymentBoxTwoColumnsLayout>
  );
};

export default PaymentBox;
